import { Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/base/alert-dialog';

interface AvisoInatividadeProps {
  /** Instante em que a sessao termina, em milissegundos da epoca. */
  encerraEm: number;
  aoContinuar: () => void;
  aoSair: () => void;
}

/**
 * Aviso de inatividade, aos 25 minutos.
 *
 * docs/AUTENTICACAO-E-ACESSO.md, secao 4: "Aviso ao usuario aos 25 minutos de
 * inatividade, com opcao de continuar."
 *
 * E um dialogo de alerta, nao um aviso discreto: interrompe de proposito, porque
 * ignorar significa perder o que estava sendo feito. O foco vai para "Continuar
 * conectado" e nao ha como fechar sem escolher — Esc e clique fora nao
 * desfazem, senao a pessoa fecharia sem entender e seria desconectada do mesmo
 * jeito.
 */
export function AvisoInatividade({ encerraEm, aoContinuar, aoSair }: AvisoInatividadeProps) {
  const [restante, setRestante] = useState(() => Math.max(0, encerraEm - Date.now()));
  const continuar = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setRestante(Math.max(0, encerraEm - Date.now()));

    const relogio = setInterval(() => {
      setRestante(Math.max(0, encerraEm - Date.now()));
    }, 1_000);

    return () => clearInterval(relogio);
  }, [encerraEm]);

  const segundos = Math.ceil(restante / 1000);
  const minutos = Math.floor(segundos / 60);
  const resto = segundos % 60;

  return (
    <AlertDialog open>
      <AlertDialogContent
        // Sem fechar por Esc nem por clique fora: a escolha e obrigatoria.
        onEscapeKeyDown={(evento) => evento.preventDefault()}
        /**
         * O foco vai para "Continuar conectado", nao para o padrao do Radix, que
         * e o botao de cancelar — Enter por reflexo desconectaria a pessoa.
         * Feito por evento de abertura, e nao com `autoFocus`: dentro de dialogo
         * modal isto e gestao de foco, o oposto do que a regra de acessibilidade
         * contra `autoFocus` protege.
         */
        onOpenAutoFocus={(evento) => {
          evento.preventDefault();
          continuar.current?.focus();
        }}
        aria-labelledby="titulo-aviso-inatividade"
      >
        <AlertDialogHeader>
          <AlertDialogTitle id="titulo-aviso-inatividade" className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-warning" aria-hidden="true" />
            Você ainda está aí?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Por segurança, sua sessão será encerrada por falta de atividade. Nada do que você fez
            até agora foi perdido.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* aria-live para quem usa leitor de tela ouvir a contagem mudar. */}
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Tempo restante:{' '}
          <strong className="font-semibold text-foreground tabular-nums">
            {minutos}:{String(resto).padStart(2, '0')}
          </strong>
        </p>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={aoSair}>Sair agora</AlertDialogCancel>
          <AlertDialogAction ref={continuar} onClick={aoContinuar}>
            Continuar conectado
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
